/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { Region } from 'src/core/types/regionTypes';
import { generateEntityColorFromString } from 'src/core/utils/color';
import { CustomClassificationResult } from 'src/customText/core/services/customTextSwagger';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { textAnalyticsDemosSupportedRegion } from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportedRegion';
import { Language, rtlLanguages, wordTokenizedLanguages } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { LinkedEntity, LinkedEntityMatch, NamedEntity } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';

const stepTheme = getScopedTheme('DemoPage');

export type LinkedEntityMatchesPair = { entity: LinkedEntity; match: LinkedEntityMatch };
export type SortTypes = 'appearanceAsc' | 'appearanceDesc' | 'confidenceAsc' | 'confidenceDesc' | 'category';
export type ResourceValidationResult = { mainErrorMessage?: React.ReactNode; secondaryErrorMessage?: string; isValid: boolean };
export type DemoId =
    | 'pii'
    | 'ner'
    | 'sentiment'
    | 'keyPhrases'
    | 'callCenter'
    | 'taForHealth'
    | 'summarization'
    | 'detectLanguage'
    | 'linkedEntities'
    | 'chatGptExpedia'
    | 'customSentiment'
    | 'textTranslation'
    | 'customHealthcare'
    | 'customTranslation'
    | 'questionsAnswering'
    | 'generateQuestions'
    | 'customConversation'
    | 'documentTranslation'
    | 'customOrchestration'
    | 'customSummarization'
    | 'customClassification'
    | 'customEntityExtraction'
    | 'documentTranslationTool'
    | 'customQuestionsAnswering';

export type EntityCategory = string;

export const getEntityId = <T extends string>(entity: NamedEntity<T> | LinkedEntityMatchesPair): string => {
    if ((entity as NamedEntity<T>).category) {
        const namedEntity = entity as NamedEntity<T>;

        return `${namedEntity.category}${namedEntity.offset}${namedEntity.length}`;
    }

    const linkedEntity = entity as LinkedEntityMatchesPair;

    return `${linkedEntity.entity.bingId}${linkedEntity.match.offset}${linkedEntity.match.length}`;
};

export const getEntityColor = <T extends string>(entityCategory: T): string => {
    switch (entityCategory.replace(' ', '')) {
        case 'Person':
            return stepTheme.personEntityColor;
        case 'PersonType':
            return stepTheme.personTypeEntityColor;
        case 'Location':
            return stepTheme.locationEntityColor;
        case 'Organization':
            return stepTheme.organizationEntityColor;
        case 'Event':
            return stepTheme.eventEntityColor;
        case 'Product':
            return stepTheme.productEntityColor;
        case 'Skill':
            return stepTheme.skillEntityColor;
        case 'Address':
            return stepTheme.addressEntityColor;
        case 'PhoneNumber':
            return stepTheme.phoneNumberEntityColor;
        case 'Email':
            return stepTheme.emailEntityColor;
        case 'URL':
            return stepTheme.urlEntityColor;
        case 'IP':
            return stepTheme.ipEntityColor;
        case 'DateTime':
            return stepTheme.dateTimeEntityColor;
        case 'Quantity':
            return stepTheme.quantityEntityColor;
        case 'Rank':
            return stepTheme.rankEntityColor;
        default:
            return generateEntityColorFromString(entityCategory);
    }
};

export const getEntitiesSortOptions = (selectedSortType: SortTypes, onSortChange: (newSort: SortTypes) => void): IContextualMenuItem[] => {
    const { localize } = useLocalization();

    return [
        {
            canCheck: true,
            key: 'appearanceAsc',
            isChecked: selectedSortType === 'appearanceAsc',
            text: localize(commonDemosMessages.appearanceAscSortOption),
            onClick: () => onSortChange('appearanceAsc')
        },
        {
            canCheck: true,
            key: 'appearanceDesc',
            isChecked: selectedSortType === 'appearanceDesc',
            text: localize(commonDemosMessages.appearanceDescSortOption),
            onClick: () => onSortChange('appearanceDesc')
        },
        {
            canCheck: true,
            key: 'confidenceDesc',
            isChecked: selectedSortType === 'confidenceDesc',
            text: localize(commonDemosMessages.confidenceDescSortOption),
            onClick: () => onSortChange('confidenceDesc')
        },
        {
            canCheck: true,
            key: 'confidenceAsc',
            isChecked: selectedSortType === 'confidenceAsc',
            text: localize(commonDemosMessages.confidenceAscSortOption),
            onClick: () => onSortChange('confidenceAsc')
        },
        {
            canCheck: true,
            key: 'category',
            isChecked: selectedSortType === 'category',
            text: localize(commonDemosMessages.categorySortOption),
            onClick: () => onSortChange('category')
        }
    ];
};

export const sortEntitiesHelper = <T extends string>(entityA: NamedEntity<T>, entityB: NamedEntity<T>, sortBy: SortTypes): number => {
    switch (sortBy) {
        case 'appearanceAsc':
            return entityA.offset < entityB.offset ? -1 : 1;
        case 'appearanceDesc':
            return entityA.offset < entityB.offset ? 1 : -1;
        case 'category':
            return entityA.category < entityB.category ? -1 : 1;
        case 'confidenceAsc':
            return entityA.confidenceScore < entityB.confidenceScore ? -1 : 1;
        case 'confidenceDesc':
            return entityA.confidenceScore < entityB.confidenceScore ? 1 : -1;
        default:
    }
};

export const sortClassesHelper = (classA: CustomClassificationResult, classB: CustomClassificationResult, sortBy: SortTypes): number => {
    switch (sortBy) {
        case 'confidenceAsc':
            return classA.confidenceScore < classB.confidenceScore ? -1 : 1;
        case 'confidenceDesc':
            return classA.confidenceScore < classB.confidenceScore ? 1 : -1;
        default:
    }
};

export const sortLinkedEntitiesHelper = (entityA: LinkedEntityMatchesPair, entityB: LinkedEntityMatchesPair, sortBy: SortTypes): number => {
    switch (sortBy) {
        case 'appearanceAsc':
            return entityA.match.offset < entityB.match.offset ? -1 : 1;
        case 'appearanceDesc':
            return entityA.match.offset < entityB.match.offset ? 1 : -1;
        case 'category':
            return entityA.entity.name < entityB.entity.name ? -1 : 1;
        case 'confidenceAsc':
            return entityA.match.confidenceScore < entityB.match.confidenceScore ? -1 : 1;
        case 'confidenceDesc':
            return entityA.match.confidenceScore < entityB.match.confidenceScore ? 1 : -1;
        default:
    }
};

const filterButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    flexContainer: { justifyContent: 'flex-start' },
    root: { minHeight: '36px', width: '100%', paddingLeft: '8px' }
});
export const getFilterCategories = <T extends { category?: string; name?: string }>(
    entities: T[],
    categoryFieldName: 'category' | 'name',
    selectedCategories: EntityCategory[] | string[],
    onSelectedCategoriesChange: (newCategories: EntityCategory[] | string[]) => void
): IContextualMenuItem[] =>
    entities
        .map(e => ({
            key: e[categoryFieldName],
            onRender: () => (
                <CommandBarButton
                    role="menuitem"
                    styles={filterButtonStyles}
                    onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        onSelectedCategoriesChange(
                            selectedCategories.some(c => c === e[categoryFieldName])
                                ? selectedCategories.filter(c => c !== e[categoryFieldName])
                                : [...selectedCategories, e[categoryFieldName]]
                        );
                    }}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                        <Checkbox checked={selectedCategories.some(c => c === e[categoryFieldName])} />
                        <LuisText>{e[categoryFieldName]}</LuisText>
                    </Stack>
                </CommandBarButton>
            )
        }))
        .filter((e, index, arr) => arr.map(a => a.key).indexOf(e.key) === index);

export const isDocumentSwaggerRtl = (language: Language['code']) => rtlLanguages.find(l => l.code === language) !== undefined;

export const isDocumentSwaggerWordTokenized = (language: Language['code']) =>
    wordTokenizedLanguages.find(l => l.code === language) !== undefined;

export const validateLanguageStudioResource = ({
    resource,
    localize,
    regionsLink,
    isPaidDemo = false,
    supportedRegions = textAnalyticsDemosSupportedRegion
}: {
    regionsLink?: string;
    isPaidDemo?: boolean;
    supportedRegions?: Region[];
    resource: AzureCsAccountStore;
    localize: (
        messageDescriptor: ReactIntl.FormattedMessage.MessageDescriptor,
        values?: { [key: string]: ReactIntl.MessageValue }
    ) => string;
}): ResourceValidationResult => {
    const isValidKind = resource?.kind === 'TextAnalytics' || resource?.kind === 'CognitiveServices';
    const isValidSku = !isPaidDemo || (isPaidDemo && resource?.sku.code === 'S');
    const isValidRegion = supportedRegions.some(region => region === resource?.region);

    if (!Boolean(resource)) {
        return {
            isValid: false,
            secondaryErrorMessage: localize(commonDemosMessages.selectLanguageStudio),
            mainErrorMessage: <LuisText>{localize(commonDemosMessages.selectResourceErrorMessage)}</LuisText>
        };
    }

    if (!isValidKind) {
        return {
            isValid: false,
            secondaryErrorMessage: localize(commonDemosMessages.selectLanguageStudio),
            mainErrorMessage: <LuisText>{localize(commonDemosMessages.resourceKindError)}</LuisText>
        };
    }

    if (!isValidSku) {
        return {
            isValid: false,
            mainErrorMessage: <LuisText>{localize(commonDemosMessages.selectPaidResourceDetailedError)}</LuisText>
        };
    }

    if (!isValidRegion) {
        return {
            isValid: false,
            secondaryErrorMessage: localize(commonDemosMessages.resourceRegionError, { region: resource.region }),
            mainErrorMessage: (
                <LuisText>
                    {localize(commonDemosMessages.resourceRegionDetailedError, { region: resource.region })}&nbsp;
                    <ExternalLink href={regionsLink}>{localize(commonDemosMessages.seeRegionsLink)}</ExternalLink>&nbsp;
                    {localize(commonDemosMessages.resourceRegionDetailedErrorPart2)}
                </LuisText>
            )
        };
    }

    return { isValid: true };
};

export const validateTranslatorResource = ({
    resource,
    localize
}: {
    resource: AzureCsAccountStore;
    localize: (
        messageDescriptor: ReactIntl.FormattedMessage.MessageDescriptor,
        values?: { [key: string]: ReactIntl.MessageValue }
    ) => string;
}): ResourceValidationResult => {
    const isValidKind = resource?.kind === 'TextTranslation';
    const isValidSku = resource?.sku.code === 'S1' || resource?.sku.code === 'D3';

    if (!Boolean(resource)) {
        return {
            isValid: false,
            secondaryErrorMessage: localize(commonDemosMessages.selectTranslator),
            mainErrorMessage: <LuisText>{localize(commonDemosMessages.selectTranslatorResourceErrorMessage)}</LuisText>
        };
    }

    if (!isValidKind) {
        return {
            isValid: false,
            secondaryErrorMessage: localize(commonDemosMessages.selectTranslator),
            mainErrorMessage: <LuisText>{localize(commonDemosMessages.translatorResourceKindError)}</LuisText>
        };
    }

    if (!isValidSku) {
        return {
            isValid: false,
            mainErrorMessage: <LuisText>{localize(commonDemosMessages.translatorResourceTierError)}</LuisText>
        };
    }

    return { isValid: true };
};

export const getBlobStoreUrl = (storageAccountId: string, containerName: string, fileName?: string) =>
    `https://ms.portal.azure.com/#view/Microsoft_Azure_Storage/${
        fileName ? 'BlobPropertiesBladeV2' : 'ContainerMenuBlade/~/overview'
    }/storageAccountId/${storageAccountId.split('/').join('%2F')}/path/${containerName.concat(
        fileName ? `%2F${fileName}/isDeleted~/false/tabToload~/3` : ''
    )}`;
